import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { PlaybackItem, Playlist } from '../../types';
import { cssClampValue } from '../../utils/common';
import AudioPlayerContext from '../AudioPlayer/AudioPlayerContext';
import playIcon from './images/play.svg';
import playWithCirclesIcon from './images/play-with-circles.svg';
import pauseIcon from './images/pause.svg';
import pauseWithCirclesIcon from './images/pause-with-circles.svg';

interface Size {
  mobile: number;
  desktop: number;
}

type Animation = 'pulse' | undefined;

function getIcon(isPlaying: boolean, iconWithCircles: boolean) {
  if (isPlaying) {
    return iconWithCircles ? pauseWithCirclesIcon : pauseIcon;
  }
  return iconWithCircles ? playWithCirclesIcon : playIcon;
}

const pulse = keyframes`
  0% {
    transform: scale(1);

  }
  75% {
    transform: scale(1.1);

  }
  100% {
    transform: scale(1);
  }
`;

const Button = styled.button<{ size: Size; animation: Animation }>`
  all: unset; // reset default button styles
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ size }) => cssClampValue(size.mobile, size.desktop)};
  height: ${({ size }) => cssClampValue(size.mobile, size.desktop)};
  transition: transform 0.12s ease-in-out;
  cursor: pointer;
  // reset pointer-events: none; from a parent element such as in the <RecordingCard /> component
  pointer-events: initial;
  ${({ animation }) =>
    animation
      ? css`
          animation: 1s 2s 2 ${pulse};
        `
      : null};

  &:hover {
    transform: scale(1.1);
    transition-timing-function: ease-out;
  }

  @media (prefers-reduced-motion) {
    animation: none;
  }
`;

const IconImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

interface Props {
  playbackItem: PlaybackItem;
  playlist?: Playlist;
  iconWithCircles?: boolean;
  /** Button size on mobile and desktop */
  size?: Size;
  renderSource?: string;
  className?: string;
  onClick?: () => void;
  animation?: Animation;
  isFocused?: boolean;
}

export default function RecordingPlayPauseButton({
  playbackItem,
  playlist = undefined,
  iconWithCircles = false,
  size = { mobile: 26, desktop: 50 },
  renderSource = undefined,
  className = undefined,
  onClick = undefined,
  animation = undefined,
  isFocused = undefined,
}: Props) {
  const {
    startPlayback,
    state,
    pause,
    play,
    currentItem,
    playlist: currentPlaylist,
  } = React.useContext(AudioPlayerContext);

  const playPauseButtonRef = React.useRef<HTMLButtonElement>(null);

  const playlistOrPlaybackItemIsActive: boolean = React.useMemo(() => {
    // if either this button or the current playback item belongs to a playlist,
    // check if they belong to same playlist & the current playback item belongs to this playlist
    if (playlist || currentPlaylist) {
      return (
        playlist?.id === currentPlaylist?.id &&
        // following check make sure the player is reset and starts playing when user clicks on
        // the play button after removing the existing current item from the playlist
        !!playlist?.items.some((item) => item.id === currentItem?.id)
      );
    }

    return currentItem?.id === playbackItem.id;
  }, [currentItem?.id, currentPlaylist, playbackItem.id, playlist]);

  const isPlaying = React.useMemo(
    () => ['playing', 'buffering'].includes(state) && playlistOrPlaybackItemIsActive,
    [state, playlistOrPlaybackItemIsActive],
  );

  const togglePlay = React.useCallback(() => {
    if (onClick) {
      onClick();
    }

    if (isPlaying) {
      pause();
      return;
    }

    if (playlistOrPlaybackItemIsActive && state === 'paused') {
      play();
      return;
    }

    startPlayback({ item: playbackItem, playlist, eventSource: renderSource });
  }, [
    onClick,
    isPlaying,
    playlistOrPlaybackItemIsActive,
    state,
    startPlayback,
    playbackItem,
    playlist,
    renderSource,
    pause,
    play,
  ]);

  React.useEffect(() => {
    const playPauseButton = playPauseButtonRef.current;
    if (isFocused && playPauseButton) {
      playPauseButton.focus();
    }
  }, [isFocused, playbackItem]);

  return (
    <Button
      type="button"
      size={size}
      className={className}
      onClick={togglePlay}
      animation={animation}
      ref={playPauseButtonRef}
      aria-label={isPlaying ? `Pause ${playbackItem.title}` : `Play ${playbackItem.title}`}
    >
      <IconImg
        src={getIcon(isPlaying, iconWithCircles)}
        width={size.mobile}
        height={size.mobile}
        alt=""
      />
    </Button>
  );
}
